/**
 * TimePanel — LawBillity time recording overlay for the Outlook Add-in
 *
 * Ported from vanilla JS: 12_time_panel.js, 13_todays_time.js
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import {
  X, Clock, Loader2, Search, ChevronDown, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const DURATION_PRESETS = [0.1, 0.2, 0.3, 0.5, 1.0, 1.5, 2.0];
const TEMPLATES = [
  { key: 'considering', label: 'Considering' },
  { key: 'drafting', label: 'Drafting' },
  { key: 'reviewing', label: 'Reviewing' },
  { key: 'attendance', label: 'Attendance' },
];

export default function TimePanel({ userId, currentEmail, bonsaiApi, selectedFolder, filingStatus, onClose }) {
  const [matterRef, setMatterRef] = useState('');
  const [matterDisplay, setMatterDisplay] = useState('');
  const [showMatterSearch, setShowMatterSearch] = useState(false);
  const [matterSearchQuery, setMatterSearchQuery] = useState('');
  const [matterResults, setMatterResults] = useState([]);
  const [searchingMatter, setSearchingMatter] = useState(false);
  const [duration, setDuration] = useState(0.1);
  const [narrative, setNarrative] = useState('');
  const [date, setDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [loadingNarrative, setLoadingNarrative] = useState(false);
  const [aiDurationHint, setAiDurationHint] = useState('');
  const [activeTemplate, setActiveTemplate] = useState('');
  const [todaysEntries, setTodaysEntries] = useState([]);
  const [todaysTotal, setTodaysTotal] = useState(0);
  const [todaysExpanded, setTodaysExpanded] = useState(true);

  const searchTimerRef = useRef(null);

  // Init panel values
  useEffect(() => {
    // Set matter from selected folder or filing status
    let ref = '';
    if (selectedFolder?.reference) ref = selectedFolder.reference;
    else if (selectedFolder?.name) ref = selectedFolder.name;
    else if (filingStatus?.filing?.matter_reference) ref = filingStatus.filing.matter_reference;

    if (ref) {
      try { ref = decodeURIComponent(ref); } catch {}
      setMatterRef(ref);
      setMatterDisplay(ref);
      setShowMatterSearch(false);
    } else {
      setMatterRef('');
      setMatterDisplay('No matter selected');
      fetchSuggestedMatter();
    }

    // Set date from email or today
    let dateStr = new Date().toISOString().split('T')[0];
    if (currentEmail?.receivedDateTime) {
      try { dateStr = new Date(currentEmail.receivedDateTime).toISOString().split('T')[0]; } catch {}
    }
    setDate(dateStr);
    setDuration(0.1);
    setNarrative('');
    setResult(null);
    setAiDurationHint('');
    setActiveTemplate('');
    loadTodaysTime();
  }, []);

  const fetchSuggestedMatter = useCallback(async () => {
    if (!currentEmail || !userId) { setShowMatterSearch(true); return; }
    try {
      const resp = await bonsaiApi.post('/time/ai-narrative', {
        subject: currentEmail.subject || '', body: '', from_address: currentEmail.from || '',
        email_id: currentEmail.id || '', template: '',
      });
      if (resp.data?.suggested_matter_reference) {
        let ref = resp.data.suggested_matter_reference;
        try { ref = decodeURIComponent(ref); } catch {}
        setMatterRef(ref);
        setMatterDisplay(ref);
        setShowMatterSearch(false);
      } else {
        setShowMatterSearch(true);
      }
    } catch { setShowMatterSearch(true); }
  }, [bonsaiApi, currentEmail, userId]);

  const generateAINarrative = useCallback(async (template) => {
    if (!currentEmail) return;
    setLoadingNarrative(true);
    setNarrative('');
    try {
      const resp = await bonsaiApi.post('/time/ai-narrative', {
        subject: currentEmail.subject || '',
        body: (currentEmail.body || '').substring(0, 2000),
        from_address: currentEmail.from || '',
        email_id: currentEmail.id || '',
        template: template || '',
      });
      if (resp.data?.narrative) setNarrative(resp.data.narrative);
      if (resp.data?.suggested_duration > 0) {
        setDuration(resp.data.suggested_duration);
        setAiDurationHint(`AI suggests ${resp.data.suggested_duration}h`);
      }
      if (resp.data?.suggested_matter_reference && !matterRef) {
        let ref = resp.data.suggested_matter_reference;
        try { ref = decodeURIComponent(ref); } catch {}
        setMatterRef(ref);
        setMatterDisplay(ref);
        setShowMatterSearch(false);
      }
    } catch {
      // Fallback narrative
      const fromName = (currentEmail.from || '').split('@')[0].replace(/\./g, ' ');
      const subj = currentEmail.subject || '';
      const templates = {
        considering: `Reviewing and considering correspondence from ${fromName} re ${subj}.`,
        drafting: `Drafting response to ${fromName} re ${subj}.`,
        reviewing: `Reviewing documents from ${fromName} re ${subj}.`,
        attendance: `Telephone attendance with ${fromName} re ${subj}.`,
      };
      setNarrative(templates[template] || `Email from ${fromName} re ${subj}.`);
    }
    setLoadingNarrative(false);
  }, [bonsaiApi, currentEmail, matterRef]);

  const setTemplate = useCallback((tpl) => {
    setActiveTemplate(tpl);
    generateAINarrative(tpl);
  }, [generateAINarrative]);

  const searchMatters = useCallback(async (query) => {
    if (query.length < 2) { setMatterResults([]); return; }
    setSearchingMatter(true);
    try {
      const resp = await bonsaiApi.get(`/filing/matters?q=${encodeURIComponent(query)}&limit=8`);
      setMatterResults(resp.data || []);
    } catch { setMatterResults([]); }
    setSearchingMatter(false);
  }, [bonsaiApi]);

  const handleMatterSearchChange = useCallback((e) => {
    const q = e.target.value;
    setMatterSearchQuery(q);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => searchMatters(q), 350);
  }, [searchMatters]);

  const selectMatter = useCallback((ref, name) => {
    try { ref = decodeURIComponent(ref); } catch {}
    try { name = decodeURIComponent(name); } catch {}
    setMatterRef(ref);
    setMatterDisplay(name || ref);
    setMatterResults([]);
    setMatterSearchQuery('');
    setShowMatterSearch(false);
  }, []);

  const saveTimeEntry = useCallback(async () => {
    let ref = matterRef;
    if (!ref && matterSearchQuery) ref = matterSearchQuery.trim();
    if (!ref) { setResult({ type: 'error', message: 'Please enter a matter reference.' }); return; }
    if (!duration || duration <= 0) { setResult({ type: 'error', message: 'Please enter a valid duration.' }); return; }
    if (!narrative.trim()) { setResult({ type: 'error', message: 'Please enter a narrative.' }); return; }
    if (!date) { setResult({ type: 'error', message: 'Please enter a date.' }); return; }

    setSaving(true);
    setResult(null);
    try {
      const resp = await bonsaiApi.post('/time/lawbillity', {
        matter_reference: ref, duration, narrative: narrative.trim(), date,
      });
      if (resp.status === 200 || resp.status === 201) {
        setResult({ type: 'success', message: `${duration}h recorded for ${ref}.` });
        loadTodaysTime();
      } else {
        setResult({ type: 'error', message: resp.data?.detail || 'Failed to save.' });
      }
    } catch (e) {
      setResult({ type: 'error', message: `Network error — your entry (${duration}h) was not lost. Please copy into LawBillity manually.` });
    }
    setSaving(false);
  }, [bonsaiApi, matterRef, duration, narrative, date, matterSearchQuery]);

  const loadTodaysTime = useCallback(async () => {
    try {
      const resp = await bonsaiApi.get('/time/recent');
      setTodaysEntries(resp.data?.entries || []);
      setTodaysTotal(resp.data?.total_hours || 0);
    } catch {}
  }, [bonsaiApi]);

  return (
    <div className="absolute inset-0 bg-navy-card/95 z-50 flex flex-col overflow-hidden" data-testid="time-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-indigo-400" />
          <span className="text-sm font-semibold text-white">Record Time</span>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-white p-1" data-testid="time-panel-close">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {/* Matter reference */}
        <div>
          <label className="text-[11px] text-gray-400 mb-1 block">Matter</label>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowMatterSearch(v => !v)}
              className="flex-1 text-left px-2.5 py-1.5 rounded border border-gray-700 bg-gray-800/60 text-xs text-white hover:border-indigo-600 transition-colors"
              data-testid="time-matter-ref">
              {matterDisplay || 'Select matter...'}
            </button>
            <button onClick={() => setShowMatterSearch(v => !v)} className="text-gray-400 hover:text-indigo-400 p-1">
              <Search className="w-3.5 h-3.5" />
            </button>
          </div>
          {showMatterSearch && (
            <div className="mt-1.5" data-testid="time-matter-search-row">
              <div className="relative">
                <Input value={matterSearchQuery} onChange={handleMatterSearchChange}
                  placeholder="Search matter..."
                  className="h-8 bg-gray-800 border-gray-700 text-xs text-white"
                  data-testid="time-matter-input" autoFocus />
                {searchingMatter && <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-indigo-400 animate-spin" />}
              </div>
              {matterResults.length > 0 && (
                <div className="mt-1 border border-gray-700 rounded bg-gray-800 max-h-32 overflow-y-auto" data-testid="time-matter-results">
                  {matterResults.map((m, i) => {
                    const name = m.matter_title || m.title || m.reference || m.client_name || 'Unknown';
                    const ref = m.reference || m.matter_title || '';
                    return (
                      <div key={i} onClick={() => selectMatter(ref, name)}
                        className="px-2 py-1.5 hover:bg-indigo-900/40 cursor-pointer border-b border-gray-800 last:border-0 text-[11px] text-white" data-testid={`matter-result-${i}`}>
                        {name}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Template buttons */}
        <div>
          <label className="text-[11px] text-gray-400 mb-1 block">Template</label>
          <div className="flex flex-wrap gap-1.5">
            {TEMPLATES.map(t => (
              <button key={t.key} onClick={() => setTemplate(t.key)}
                className={`time-tpl-btn px-2.5 py-1 text-[11px] rounded border transition-colors ${activeTemplate === t.key ? 'bg-indigo-800/60 text-indigo-300 border-indigo-700' : 'bg-gray-800 text-gray-300 border-gray-700 hover:border-gray-600'}`}
                data-testid={`time-tpl-${t.key}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Date */}
        <div>
          <label className="text-[11px] text-gray-400 mb-1 block">Date</label>
          <Input type="date" value={date} onChange={e => setDate(e.target.value)}
            className="h-8 bg-gray-800 border-gray-700 text-xs text-white" data-testid="time-date" />
        </div>

        {/* Duration */}
        <div>
          <label className="text-[11px] text-gray-400 mb-1 block">Duration (hours)</label>
          <div className="flex flex-wrap gap-1.5 mb-1.5">
            {DURATION_PRESETS.map(d => (
              <button key={d} onClick={() => setDuration(d)}
                className={`time-dur-btn px-2.5 py-1 text-[11px] rounded border transition-colors ${duration === d ? 'bg-indigo-600 text-white border-indigo-500' : 'bg-indigo-800/60 text-indigo-300 border-indigo-700 hover:border-indigo-600'}`}
                data-testid={`time-dur-${d}`}>
                {d}h
              </button>
            ))}
          </div>
          <Input type="number" value={duration} onChange={e => setDuration(parseFloat(e.target.value) || 0)} step="0.1" min="0"
            className="h-8 bg-gray-800 border-gray-700 text-xs text-white w-24" data-testid="time-duration" />
          {aiDurationHint && <p className="text-[10px] text-indigo-400 mt-0.5" data-testid="time-ai-duration-hint">{aiDurationHint}</p>}
        </div>

        {/* Narrative */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[11px] text-gray-400">Narrative</label>
            <button onClick={() => generateAINarrative(activeTemplate)} className="flex items-center gap-1 text-[10px] text-indigo-400 hover:text-indigo-300" data-testid="time-ai-narrative-btn">
              <Sparkles className="w-3 h-3" />AI Generate
            </button>
          </div>
          {loadingNarrative && (
            <div className="flex items-center gap-2 p-2 mb-1" data-testid="time-narrative-loading">
              <Loader2 className="w-3.5 h-3.5 text-indigo-400 animate-spin" />
              <span className="text-[11px] text-gray-400">Generating...</span>
            </div>
          )}
          <textarea value={narrative} onChange={e => setNarrative(e.target.value)} rows={3}
            className="w-full rounded border border-gray-700 bg-gray-800 text-xs text-white p-2 resize-none focus:border-indigo-600 focus:outline-none focus:ring-1 focus:ring-indigo-600"
            placeholder="Enter time narrative..." data-testid="time-narrative" />
        </div>

        {/* Save button */}
        <Button onClick={saveTimeEntry} disabled={saving}
          className="w-full bg-indigo-600 hover:bg-indigo-500 text-white h-9"
          data-testid="time-save-btn">
          {saving ? 'Saving...' : 'Save to LawBillity'}
        </Button>

        {/* Result */}
        {result && (
          <div className={`text-xs p-2 rounded border ${result.type === 'success' ? 'bg-status-green-dim/30 border-green-700 text-status-green' : 'bg-status-red-dim/30 border-red-700 text-status-red'}`} data-testid="time-result">
            {result.message}
          </div>
        )}

        {/* Today's Time */}
        <div className="border-t border-gray-700 pt-2" data-testid="todays-time">
          <button onClick={() => setTodaysExpanded(v => !v)}
            className="flex items-center gap-2 w-full text-left" data-testid="todays-time-toggle">
            <Clock className="w-3.5 h-3.5 text-indigo-400" />
            <span className="text-xs font-semibold text-indigo-300">Today's Time</span>
            <span className="text-xs text-indigo-400 ml-auto" data-testid="todays-time-total">{todaysTotal.toFixed(1)}h</span>
            <ChevronDown className={`w-3 h-3 text-gray-500 transition-transform ${todaysExpanded ? '' : '-rotate-90'}`} />
          </button>
          {todaysExpanded && (
            <div className="mt-1.5 space-y-1" data-testid="todays-time-list">
              {todaysEntries.length === 0 ? (
                <div className="text-[10px] text-gray-500 py-1">No time recorded today</div>
              ) : todaysEntries.map((e, i) => (
                <div key={i} className="flex items-center gap-2 py-1 px-1.5 rounded bg-gray-800/40 border border-gray-700/40" data-testid="time-entry-row">
                  <span className="text-[10px] font-mono text-indigo-300 flex-shrink-0 w-8 text-right">{e.duration.toFixed(1)}h</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] text-white truncate">{e.matter_reference}</div>
                    <div className="text-[9px] text-gray-500 truncate">{e.narrative || ''}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
